// src/components/sales/sections/HeatmapSection.jsx
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Settings, AlertCircle } from 'lucide-react';
import salesService from '../../../services/salesService';

const COLOR_PALETTE = {
  color1: { r:217, g:83,  b:79  },
  color2: { r:244, g:164, b:96  },
  color3: { r:255, g:235, b:59  },
  color4: { r:147, g:196, b:125 },
  color5: { r:87,  g:166, b:74  },
};

const getGradientCSS = () =>
  `linear-gradient(to right, ${Object.values(COLOR_PALETTE).map(c=>`rgb(${c.r},${c.g},${c.b})`).join(',')})`;

// Always map percentage on a fixed 0–100 scale so identical percentages
// always produce identical colors regardless of which column they're in.
const getIntensityColor = (percentage) => {
  const ratio = Math.max(0, Math.min(1, (percentage || 0) / 100));
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const seg  = Math.floor(ratio * 4);
  const t    = (ratio * 4) - seg;
  const keys = ['color1', 'color2', 'color3', 'color4', 'color5'];
  const from = COLOR_PALETTE[keys[Math.min(seg, 3)]];
  const to   = COLOR_PALETTE[keys[Math.min(seg + 1, 4)]];
  return {
    backgroundColor: `rgb(${lerp(from.r,to.r,t)},${lerp(from.g,to.g,t)},${lerp(from.b,to.b,t)})`,
    color: '#ffffff',
  };
};

const HeatmapSection = ({ collapsed, setCollapsed }) => {
  const [activeTab,       setActiveTab]       = useState('sales_calls');
  const [activeDimension, setActiveDimension] = useState('call_type');
  const [data,            setData]            = useState(null);
  const [dimensions,      setDimensions]      = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [dateRange,       setDateRange]       = useState('last_8_weeks');

  const tabs = [
    { id:'sales_calls',  label:'Sales Calls'  },
    { id:'offers_sent',  label:'Offers Sent'  },
    { id:'closed_deals', label:'Closed Deals' },
  ];

  const dateRangeOptions = [
    { value:'last_4_weeks',  label:'4 Weeks'  },
    { value:'last_8_weeks',  label:'8 Weeks'  },
    { value:'last_12_weeks', label:'12 Weeks' },
    { value:'all_weeks',     label:'All Time' },
  ];

  useEffect(() => {
    const fetch = async () => {
      try {
        setError(null);
        const res = await salesService.getHeatmapDimensions(activeTab);
        const dims = res?.dimensions || [];
        setDimensions(dims);
        if (dims.length > 0) setActiveDimension(dims[0].id);
      } catch (e) {
        setError(`Failed to load dimensions: ${e.message}`);
        setDimensions([]);
        setActiveDimension('call_type');
      }
    };
    fetch();
  }, [activeTab]);

  useEffect(() => {
    if (!activeDimension) return;
    const fetch = async () => {
      setLoading(true); setError(null);
      try {
        const res = await salesService.getHeatmapData(activeTab, { dimension:activeDimension, date_range:dateRange, closer:'all', aggregation_level:'weekly', periods_back:8 });
        setData(res?.data || res || null);
      } catch (e) {
        setError(`Failed to load heatmap: ${e.message}`);
        setData(null);
      } finally { setLoading(false); }
    };
    fetch();
  }, [activeTab, activeDimension, dateRange]);

  const switchTab = (id) => { setActiveTab(id); setData(null); setError(null); setActiveDimension(''); };
  const switchDim = (id) => { setActiveDimension(id); setData(null); setError(null); };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-grey-200 overflow-hidden">
      {/* ── Section header ──────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-5 py-3 bg-darkblue-500 cursor-pointer"
        onClick={() => setCollapsed?.(!collapsed)}
      >
        <h3 className="text-sm font-semibold text-white">Heatmap Analysis</h3>
        {collapsed
          ? <ChevronRight className="h-4 w-4 text-white/70" />
          : <ChevronDown  className="h-4 w-4 text-white/70" />}
      </div>

      {!collapsed && (
        <div className="p-5">
          {/* Error banner */}
          {error && (
            <div className="bg-pink-50 border border-pink-200 rounded-lg px-4 py-2.5 flex items-center gap-2 mb-4">
              <AlertCircle className="h-4 w-4 text-pink-600 flex-shrink-0" />
              <span className="text-xs text-pink-700">{error}</span>
              <button onClick={()=>setError(null)} className="ml-auto text-pink-400 hover:text-pink-600 text-sm">×</button>
            </div>
          )}

          {/* Top row — tabs + date range */}
          <div className="flex items-center justify-between mb-4 gap-4">
            <div className="flex gap-2">
              {tabs.map(t => (
                <button key={t.id} onClick={()=>switchTab(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    activeTab===t.id ? 'bg-brand-500 text-white' : 'bg-grey-100 text-darkblue-500 hover:bg-grey-200'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex gap-1.5">
              {dateRangeOptions.map(o => (
                <button key={o.value} onClick={()=>setDateRange(o.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                    dateRange===o.value ? 'bg-darkblue-500 text-white' : 'bg-grey-100 text-darkblue-500 hover:bg-grey-200'
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dimension toggle */}
          {dimensions.length > 1 && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-grey-50 rounded-lg">
              <Settings size={14} className="text-grey-400" />
              <span className="text-xs font-semibold text-darkblue-500">View by:</span>
              <div className="flex gap-1.5">
                {dimensions.map(d => (
                  <button key={d.id} onClick={()=>switchDim(d.id)} title={d.description}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                      activeDimension===d.id
                        ? 'bg-brightblue-50 text-brightblue-700 border-brightblue-300'
                        : 'bg-white text-darkblue-500 border-grey-300 hover:bg-grey-50'
                    }`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center h-48">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                <div className="text-xs text-grey-400">Loading heatmap…</div>
              </div>
            </div>
          )}

          {/* Table */}
          {!loading && data?.headers && data?.rows?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-grey-200">
                    <th className="text-left p-3 font-semibold text-darkblue-500 bg-grey-50 w-32">Time Period</th>
                    {data.headers.map((h, i) => (
                      <th key={i} className="text-center p-3 font-semibold text-darkblue-500 bg-grey-50 min-w-[80px]">{h}</th>
                    ))}
                    <th className="text-center p-3 font-semibold text-darkblue-500 bg-grey-50 w-20">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, ri) => (
                    <tr key={ri} className="border-b border-grey-100 hover:bg-grey-50">
                      <td className="p-3 font-semibold text-darkblue-500 w-32">{row.week || row.label || `Week ${ri + 1}`}</td>
                      {row.cells?.map((cell, ci) => {
                        // Color is driven purely by percentage on a fixed 0–100 scale
                        const style = getIntensityColor(cell.percentage || 0);
                        return (
                          <td key={ci} className="p-1 text-center">
                            <div
                              className="w-full h-14 rounded-md flex flex-col items-center justify-center font-bold transition-all hover:scale-105 cursor-pointer min-w-[64px]"
                              style={style}
                              title={`${row.week} - ${data.headers[ci]}: ${cell.value} (${Math.round(cell.percentage || 0)}%)`}
                            >
                              <div className="text-sm">{cell.value || 0}</div>
                              <div className="text-xs opacity-80">{Math.round(cell.percentage || 0)}%</div>
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-3 text-center font-semibold text-darkblue-500 w-20">{row.total || 0}</td>
                    </tr>
                  ))}
                  {data.totals && (
                    <tr className="border-t-2 border-darkblue-200 bg-grey-50 font-semibold">
                      <td className="p-3 text-darkblue-500">Total</td>
                      {data.totals.map((t, i) => <td key={i} className="p-3 text-center text-darkblue-500">{t}</td>)}
                      <td className="p-3 text-center text-darkblue-500">{data.totals.reduce((s, t) => s + t, 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : !loading && (
            <div className="flex items-center justify-center h-48 text-grey-400">
              <div className="text-center text-xs">
                {!activeDimension ? <p>Select a dimension to view data</p>
                  : data?.empty ? <p>No data for this period</p>
                  : <p>No data to display</p>}
                <p className="mt-1 text-grey-300">Tab: {activeTab} | Dimension: {activeDimension}</p>
              </div>
            </div>
          )}

          {/* Legend — fixed 0–100% scale */}
          {!loading && data?.headers && data?.rows && (
            <div className="mt-5 text-xs text-grey-400">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-darkblue-500 mb-2">Color Scale</p>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-4 rounded" style={{ background: getGradientCSS() }} />
                    <span>0% → 100%</span>
                  </div>
                </div>
                <div className="text-right">
                  <p>{data?.dimension?.replace('_', ' ').toUpperCase()}</p>
                  <p>Records: {data?.metadata?.total_records || 0}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeatmapSection;